/**
 * Project Name: Brainsie eCommerce v4
 * Component: Simple Tab
 * Description: Individual tab content with image left, text content right layout
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export default function SimpleTab({
  component,
  section,
  darkMode,
  theme,
  isActiveTab,
}) {
  // Get component data
  const heading = component.Primary_Text || "";
  const paragraphText = component.Secondary_Text || "";
  const descriptiveText = component.Descriptive_Text || "";

  // Try to parse settings from Component_Settings
  const componentSettings = component.Component_Settings
    ? component.Component_Settings.startsWith("{")
      ? JSON.parse(component.Component_Settings)
      : {}
    : {};

  // Helper function to get theme-aware text color
  const getTextColor = () => {
    if (componentSettings.textColor) {
      return componentSettings.textColor;
    }
    return darkMode ? theme.palette.common.white : theme.palette.text.primary;
  };

  // Get image URL
  let imageUrl = null;
  if (component.Media_Files) {
    let mediaFile;
    if (Array.isArray(component.Media_Files)) {
      mediaFile = component.Media_Files[0];
    } else if (typeof component.Media_Files === "string") {
      try {
        const parsed = JSON.parse(component.Media_Files);
        mediaFile = Array.isArray(parsed) ? parsed[0] : parsed;
      } catch {
        mediaFile = component.Media_Files;
      }
    }

    if (mediaFile) {
      mediaFile = mediaFile.trim();
      const baseUrl = process.env.BASEURL || "";
      if (baseUrl.endsWith("/") && mediaFile.startsWith("/")) {
        imageUrl = baseUrl + mediaFile.substring(1);
      } else if (!baseUrl.endsWith("/") && !mediaFile.startsWith("/")) {
        imageUrl = baseUrl + "/" + mediaFile;
      } else {
        imageUrl = baseUrl + mediaFile;
      }
    }
  }

  // Parse list items from List_Content (JSON array)
  const parseListItems = () => {
    if (!component.List_Content) return [];

    try {
      const items = JSON.parse(component.List_Content);
      if (Array.isArray(items) && items.length > 0) {
        return items.filter((item) => item && item.trim() !== "");
      }
    } catch (error) {
      console.warn("Error parsing List_Content in SimpleTab:", error);
      // Fallback: treat as single item if not valid JSON
      if (component.List_Content.trim() !== "") {
        return [component.List_Content];
      }
    }
    return [];
  };

  const listItems = parseListItems();

  return (
    <Grid container spacing={4} alignItems="flex-start">
      {/* Left Column - Image */}
      <Grid item xs={12} md={5}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            backgroundColor:
              componentSettings.imageBg || theme.palette.grey[100],
            borderRadius: componentSettings.imageRadius || 2,
            p: 2,
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={heading || "Tab content"}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "400px",
                objectFit: "contain",
                borderRadius: componentSettings.imageRadius || 1,
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "300px",
                backgroundColor: theme.palette.grey[200],
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.text.secondary,
              }}
            >
              <Typography variant="body2">Image</Typography>
            </Box>
          )}
        </Box>
      </Grid>

      {/* Right Column - Content */}
      <Grid item xs={12} md={7}>
        <Box sx={{ pl: { md: 2 } }}>
          {/* Tab Heading */}
          {heading && heading !== "NA" && (
            <Typography
              variant="h4"
              component="h3"
              sx={{
                mb: 3,
                color: componentSettings.headingColor || getTextColor(),
                fontWeight: 600,
                fontSize: { xs: "1.5rem", md: "2rem" },
                lineHeight: 1.3,
              }}
            >
              {heading}
            </Typography>
          )}

          {/* Paragraph Text */}
          {paragraphText && paragraphText !== "NA" && (
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: componentSettings.textColor || getTextColor(),
                lineHeight: 1.6,
                fontSize: { xs: "1rem", md: "1.1rem" },
                opacity: 0.85,
              }}
            >
              {paragraphText}
            </Typography>
          )}

          {/* Descriptive Text */}
          {descriptiveText && descriptiveText !== "NA" && (
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: componentSettings.textColor || getTextColor(),
                lineHeight: 1.6,
                fontSize: { xs: "1rem", md: "1.1rem" },
                opacity: 0.85,
              }}
            >
              {descriptiveText}
            </Typography>
          )}

          {/* List Items */}
          {listItems.length > 0 && (
            <List sx={{ p: 0, mb: 3 }}>
              {listItems.map((item, index) => (
                <ListItem key={index} sx={{ pl: 0, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon
                      sx={{
                        color:
                          componentSettings.iconColor ||
                          theme.palette.success.main,
                        fontSize: "1.2rem",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: {
                        color:
                          componentSettings.listTextColor || getTextColor(),
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        lineHeight: 1.5,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Optional CTA or additional content can be added here */}
        </Box>
      </Grid>
    </Grid>
  );
}
